import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { FLAT_RATE_PRICING_ITEM_TYPE, PER_UNIT_PRICING_ITEM_TYPE } from '@atlassian/mpac-types';
import { MarketplacePricingTierPolicy } from '@atlassian/mpac-types/graphql-gateway-types';
import { List, Record as ImmutableRecord } from 'immutable';
export const PricingTypes = Object.freeze({
  TOTAL_TIER: 'TOTAL_TIER',
  PER_UNIT_TIER: 'PER_UNIT_TIER'
});
export const getTierDescription = function getTierDescription(priceType, unitCount) {
  let prevUnitCount = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  if (unitCount === -1 && prevUnitCount === 5000) {
    return 'Unlimited users';
  } else if (unitCount === -1) {
    // In case of 20k , last 2 tier difference is 5k, it will be same for further new upcoming tiers ( 25k, 30k , 35k , 40k , 45k and 50k ).
    // Instead of showing unlimited, last tier should be 15001 to 20000 users
    return "".concat(prevUnitCount + 1, " - ").concat(prevUnitCount + 5000, " users");
  }
  if (priceType === PricingTypes.TOTAL_TIER) {
    return "".concat(unitCount, " users");
  }
  return "".concat(prevUnitCount + 1, " - ").concat(unitCount, " users");
};
export const getFlattenedAppPrice = appsPricing => appsPricing.map(pricing => {
  const priceType = pricing.perUnitItems ? PricingTypes.PER_UNIT_TIER : PricingTypes.TOTAL_TIER;
  const priceItems = pricing.perUnitItems || pricing.items;
  const flattenAppsPricing = priceItems.map((item, index) => getAppData(priceType, item, index === 0 ? 0 : priceItems[index - 1].unitCount));
  if (priceType === PricingTypes.PER_UNIT_TIER) {
    flattenAppsPricing.unshift(getAppData(PricingTypes.TOTAL_TIER, pricing.items[0]));
  }
  function getAppData(PricingType, item, prevUnitCount) {
    return {
      priceType: PricingType,
      unitCount: item.unitCount,
      amount: item.localCurrencyAmount,
      description: getTierDescription(PricingType, item.unitCount, prevUnitCount),
      localCurrency: item.localCurrency
    };
  }
  return flattenAppsPricing;
});
const getAppsTotalPricing = flattenAppPrice => flattenAppPrice.reduce((accumulatedPrice, currentPrice) => accumulatedPrice.map((price, index) => _objectSpread(_objectSpread({}, price), {}, {
  amount: price.amount + currentPrice[index].amount
})));
export const applyFlatDiscount = (appsTotalPrice, flatDiscount) => {
  let previousMaxTierPrice;
  let previousUnitCount;
  // in case of 10k or 20k , last 2 tier difference is 5k, it will be same for further new tiers whenever added
  const lastTwoTierDifference = 5000;
  return appsTotalPrice.map(priceItem => {
    let maxTieredAmount = priceItem.amount;
    if (priceItem.priceType === PricingTypes.PER_UNIT_TIER) {
      const currentUnitCount = priceItem.unitCount === -1 ? lastTwoTierDifference + previousUnitCount : priceItem.unitCount;
      if (previousUnitCount && previousMaxTierPrice) {
        maxTieredAmount = previousMaxTierPrice + (currentUnitCount - previousUnitCount) * priceItem.amount;
      } else {
        maxTieredAmount = currentUnitCount * priceItem.amount;
      }
      previousUnitCount = priceItem.unitCount;
    }
    previousMaxTierPrice = maxTieredAmount;
    const discountAmount = maxTieredAmount * flatDiscount / 100;
    return _objectSpread(_objectSpread({}, priceItem), {}, {
      discountPercentage: flatDiscount,
      discountAmount,
      totalAmount: maxTieredAmount - discountAmount
    });
  });
};
export const getAppsPricingWithDiscount = (appsPricing, flatDiscount) => {
  const flattenAppPrice = getFlattenedAppPrice(appsPricing);
  const appsTotalPrice = getAppsTotalPricing(flattenAppPrice);
  return applyFlatDiscount(appsTotalPrice, flatDiscount);
};
export const FREE_PRICING_TYPE = 'FREE';
export const PAID_PRICING_TYPE = 'PAID';
export const makePaidAppPricingItem = ImmutableRecord({
  amount: 0,
  currencyCode: '',
  unitCount: 0,
  type: PER_UNIT_PRICING_ITEM_TYPE
});
export const makePaidAppPricing = ImmutableRecord({
  type: PAID_PRICING_TYPE,
  annual: List(),
  monthly: List()
});
export const PricingItemMap = {
  [MarketplacePricingTierPolicy.Block]: FLAT_RATE_PRICING_ITEM_TYPE,
  [MarketplacePricingTierPolicy.PerUnit]: PER_UNIT_PRICING_ITEM_TYPE
};
const mapPricingItems = pricingPlan => {
  var _pricingPlan$tieredPr;
  return (_pricingPlan$tieredPr = pricingPlan === null || pricingPlan === void 0 ? void 0 : pricingPlan.tieredPricing.items.map(item => {
    var _item$ceiling;
    return makePaidAppPricingItem({
      amount: item.amount,
      currencyCode: pricingPlan.currency,
      unitCount: (_item$ceiling = item.ceiling) !== null && _item$ceiling !== void 0 ? _item$ceiling : item.floor,
      type: PricingItemMap[item.policy]
    });
  })) !== null && _pricingPlan$tieredPr !== void 0 ? _pricingPlan$tieredPr : [];
};
export const getPricingPlanItems = appPricing => {
  const {
    monthlyPricingPlan,
    annualPricingPlan
  } = appPricing;
  return {
    monthly: mapPricingItems(monthlyPricingPlan),
    annual: mapPricingItems(annualPricingPlan)
  };
};